import { useEffect, useMemo, useState } from "react";
import { ValorantApi } from "./api";
import { AgentAbility, ValorantAgent } from "./types";

export type AbilityInfo = {
    name: string;
    description: string;
    icon: string | null;
};

let agentsPromise: Promise<ValorantAgent[]> | null = null;
let agentsCache: ValorantAgent[] | null = null;

function loadAgents() {
    if (!agentsPromise) {
        agentsPromise = ValorantApi.getAgents()
            .then((response) => {
                agentsCache = response.data || [];
                return agentsCache;
            })
            .catch(() => {
                agentsPromise = null;
                return [] as ValorantAgent[];
            });
    }
    return agentsPromise;
}

function canonName(name?: string | null) {
    return (name || "").trim().toLowerCase().replace(/[^a-z0-9]/g, "");
}

function canonSlot(slot?: string | null) {
    const s = (slot || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
    if (!s) return "";
    if (s.includes("ULT")) return "ULTIMATE";
    if (s.includes("GRENADE") || s === "C" || s === "ABILITY3") return "GRENADE";
    if (s === "ABILITY1" || s === "Q") return "ABILITY1";
    if (s === "ABILITY2" || s === "E") return "ABILITY2";
    return s;
}

function matchAbility(agentName: string, slot: string, agents: ValorantAgent[]): AbilityInfo | null {
    const agent = agents.find((entry) => canonName(entry.displayName) === canonName(agentName));
    if (!agent?.abilities?.length) return null;
    const target = canonSlot(slot);
    if (!target) return null;
    const ability = agent.abilities.find((entry: AgentAbility) => entry?.slot && canonSlot(entry.slot) === target);
    if (!ability) return null;
    return {
        name: ability.displayName,
        description: ability.description,
        icon: ability.displayIcon,
    };
}

export function useAbilityInfo(agentName?: string, slot?: string): AbilityInfo | null {
    const [agents, setAgents] = useState<ValorantAgent[] | null>(agentsCache);

    useEffect(() => {
        if (agentsCache) {
            setAgents(agentsCache);
            return;
        }
        let cancelled = false;
        loadAgents().then((data) => {
            if (!cancelled) setAgents(data);
        });
        return () => {
            cancelled = true;
        };
    }, []);

    return useMemo(() => {
        if (!agentName || !slot || !agents) return null;
        try {
            return matchAbility(agentName, slot, agents);
        } catch {
            return null;
        }
    }, [agentName, slot, agents]);
}
